<template>
  <main id="page-activity-center" class="news-page activity-center">

    <!-- ── Masthead ─────────────────────────────── -->
    <header class="masthead masthead--compact">
      <div class="masthead__rule masthead__rule--thick"></div>
      <div class="masthead__rule masthead__rule--thin"></div>
    </header>

    <!-- ── Not authenticated ──────────────────── -->
    <div v-if="!currentUser" class="ac-empty-state">
      Trebuie să fii autentificat pentru a accesa centrul de activitate.
    </div>

    <!-- ── Content area ───────────────────────── -->
    <div v-else class="ac-content">

      <!-- ── Tab 1: Notificări ──────────────────── -->
      <div v-if="activeTab === 'notifications'" class="ac-tab-panel">
        <div class="ac-panel-header">
          <span class="ac-panel-title">Notificări</span>
          <button v-if="unreadCount > 0" type="button" class="ac-mark-all-btn" :disabled="markingAll" @click="markAllRead">
            Marchează toate ca citite
          </button>
        </div>

        <div v-if="loading" class="ac-state">Se încarcă...</div>
        <div v-else-if="fetchError" class="ac-state ac-state--error">
          Nu s-au putut încărca notificările.
          <button type="button" class="ac-retry-btn" @click="fetchNotifications">Reîncearcă</button>
        </div>
        <div v-else-if="notifications.length === 0" class="ac-state">Nu ai notificări.</div>

        <ul v-else class="ac-notif-list">
          <li v-for="notif in notifications" :key="notif.id" class="ac-notif-item" :class="{ 'ac-notif-item--unread': !notif.read_at }" @click="handleNotifClick(notif)">
            <span v-if="!notif.read_at" class="ac-notif-item__dot" aria-hidden="true"></span>
            <div class="ac-notif-item__body">
              <div class="ac-notif-item__message">{{ notif.message }}</div>
              <blockquote v-if="notif.preview" class="ac-notif-item__preview" v-html="notif.preview"></blockquote>
              <div class="ac-notif-item__time">{{ formatTime(notif.created_at) }}</div>
            </div>
          </li>
        </ul>
      </div>

      <!-- ── Tab 2: Comentariile mele ──────────── -->
      <div v-else-if="activeTab === 'comments'" class="ac-tab-panel">
        <div class="ac-panel-header">
          <span class="ac-panel-title">Comentariile mele</span>
        </div>
        <div class="ac-state">În curând.</div>
      </div>

      <!-- ── Tab 3: Sondaje ─────────────────────── -->
      <div v-else-if="activeTab === 'polls'" class="ac-tab-panel">
        <div class="ac-panel-header">
          <span class="ac-panel-title">Sondajele mele</span>
        </div>
        <div class="ac-state">În curând.</div>
      </div>

      <!-- ── Tab 4: Favorite ───────────────────── -->
      <div v-else-if="activeTab === 'bookmarks'" class="ac-tab-panel">
        <div class="ac-panel-header">
          <span class="ac-panel-title">Favorite</span>
        </div>
        <div class="ac-state">În curând.</div>
      </div>

    </div>

    <!-- ── Bottom tab bar ─────────────────────── -->
    <nav v-if="currentUser" class="ac-tab-bar" role="tablist" aria-label="Centrul de activitate">

      <!-- Notificări -->
      <button type="button" role="tab" class="ac-tab" :class="{ 'ac-tab--active': activeTab === 'notifications' }" :aria-selected="activeTab === 'notifications'" @click="selectTab('notifications')">
        <span class="ac-tab__icon" aria-hidden="true">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
            <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
          </svg>
        </span>
        <span class="ac-tab__label">Notificări</span>
        <span v-if="unreadCount > 0" class="ac-tab__badge">{{ unreadCount > 99 ? '99+' : unreadCount }}</span>
      </button>

      <!-- Comentariile mele -->
      <button type="button" role="tab" class="ac-tab" :class="{ 'ac-tab--active': activeTab === 'comments' }" :aria-selected="activeTab === 'comments'" @click="selectTab('comments')">
        <span class="ac-tab__icon" aria-hidden="true">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
          </svg>
        </span>
        <span class="ac-tab__label">Comentarii</span>
      </button>

      <!-- Sondaje -->
      <button type="button" role="tab" class="ac-tab" :class="{ 'ac-tab--active': activeTab === 'polls' }" :aria-selected="activeTab === 'polls'" @click="selectTab('polls')">
        <span class="ac-tab__icon" aria-hidden="true">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <line x1="18" y1="20" x2="18" y2="10"/>
            <line x1="12" y1="20" x2="12" y2="4"/>
            <line x1="6" y1="20" x2="6" y2="14"/>
          </svg>
        </span>
        <span class="ac-tab__label">Sondaje</span>
      </button>

      <!-- Favorite -->
      <button type="button" role="tab" class="ac-tab" :class="{ 'ac-tab--active': activeTab === 'bookmarks' }" :aria-selected="activeTab === 'bookmarks'" @click="selectTab('bookmarks')">
        <span class="ac-tab__icon" aria-hidden="true">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z"/>
          </svg>
        </span>
        <span class="ac-tab__label">Favorite</span>
      </button>

    </nav>

  </main>
</template>

<script>
export default {
  name: 'ActivityCenterView',

  data() {
    return {
      currentUser: null,
      activeTab: 'notifications',
      loading: false,
      fetchError: false,
      markingAll: false,
      notifications: [],
      unreadCount: 0,
    };
  },

  async created() {
    await this.fetchCurrentUser();
    if (this.currentUser) {
      this.fetchNotifications();
    }
  },

  methods: {
    async fetchCurrentUser() {
      try {
        const resp = await window.axios.get('/user');
        this.currentUser = resp.data;
      } catch {
        this.currentUser = null;
      }
    },

    selectTab(tabId) {
      this.activeTab = tabId;
    },

    async fetchNotifications() {
      this.loading = true;
      this.fetchError = false;
      try {
        const resp = await window.axios.get('/notifications');
        this.notifications = resp.data;
        this.unreadCount = this.notifications.filter(n => !n.read_at).length;
      } catch {
        this.fetchError = true;
      } finally {
        this.loading = false;
      }
    },

    async markAllRead() {
      this.markingAll = true;
      try {
        await window.axios.post('/notifications/read-all');
        this.notifications = this.notifications.map(n => ({ ...n, read_at: n.read_at || new Date().toISOString() }));
        this.unreadCount = 0;
      } catch {
        // silent
      } finally {
        this.markingAll = false;
      }
    },

    async handleNotifClick(notif) {
      if (!notif.read_at) {
        try {
          await window.axios.post(`/notifications/${notif.id}/read`);
          notif.read_at = new Date().toISOString();
          this.unreadCount = Math.max(0, this.unreadCount - 1);
        } catch {
          // silent
        }
      }

      if (notif.href) {
        this.$router.push(notif.href);
      }
    },

    formatTime(ts) {
      if (!ts) return '';
      const d = new Date(ts);
      const now = new Date();
      const diffMs = now - d;
      const diffMins = Math.floor(diffMs / 60000);
      if (diffMins < 1) return 'Acum';
      if (diffMins < 60) return `Acum ${diffMins} min`;
      const diffHours = Math.floor(diffMins / 60);
      if (diffHours < 24) return `Acum ${diffHours} h`;
      const diffDays = Math.floor(diffHours / 24);
      return `Acum ${diffDays} zile`;
    },
  },
};
</script>
